import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap-5';
import { Button } from 'components/ui/button';
import MyPropertiesTable from './my_properties_table';
import { debounce } from 'lodash';
import { myPropertiesByAddress } from '../../helpers/prospect_api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import Paginator from '../paginator';
import EmptyState from '../empty_state';
import AddToList from '../properties/add_to_list';
import { Provider } from 'react-redux';
import configureStore from 'bundles/connect/components/store/configure';
import OffMarketPropertyOffcanvas from '../search/components/off_market_property_offcanvas';
import { type ConnectProperty, type Profile } from 'bundles/connect/types';

type Props = {
  profile: Profile;
}

export default function MyPropertiesIndex({ profile }: Props) {
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [properties, setProperties] = useState<ConnectProperty[]>();
  const [selectedProperties, setSelectedProperties] = useState<ConnectProperty[]>([]);

  const store = useMemo(() => configureStore(
    {
      ui: {
        profile,
      }
    }
  ), []);

  useEffect(() => {
    debouncedSearch(searchText, page);
  }, [searchText, page]);

  const onUpdate = useCallback((e) => {
    setPage(1);
    setSearchText(e.target.value);
  }, []);

  const debouncedSearch = useCallback(debounce((address, page) => {
    myPropertiesByAddress(address, page)
      .then((r) => {
        setProperties(r.properties);
        setTotal(r.total);
      });
  }, 300), []);

  const handleSelectAll = useCallback(() => {
    if (selectedProperties.length < (properties || []).length) {
      setSelectedProperties(properties || []);
    } else {
      setSelectedProperties([]);
    }
  }, [properties, selectedProperties]);

  const handleSelect = useCallback((property) => {
    const propertyExists = selectedProperties.some(prop => prop.propertyId === property.propertyId);
    let newCheckedProperties: ConnectProperty[];

    if (propertyExists) {
      newCheckedProperties = selectedProperties.filter(prop => prop.propertyId !== property.propertyId);
    } else {
      newCheckedProperties = selectedProperties.concat(property);
    }

    setSelectedProperties(newCheckedProperties);
  }, [selectedProperties]);

  return (
    <Provider store={store}>
      <Card className="shadow">
        <Card.Header>
          <Row>
            <Col sm={6} xs={8} >
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faMagnifyingGlass}/>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search for a property name"
                  type="text"
                  value={searchText}
                  onChange={e => onUpdate(e)}
                />
              </InputGroup>
            </Col>
            <Col className="ml-auto text-end" sm={6} xs={4} >
              <AddToList
                itemCount={selectedProperties.length}
                itemIds={new Set(selectedProperties.map(property => property.insightsPropertyId))}
                tooltip={false}
              >
                <Button className="text-nowrap px-2" disabled={selectedProperties.length === 0} label="+ Add to List" />
              </AddToList>
            </Col>
          </Row>
        </Card.Header>
        {properties && properties.length === 0 && (
          <EmptyState
            icon={faBuilding}
            text={searchText ? '' : 'Your bookmarked properties will display here.'}
            title={searchText ? 'No Results Found' : ''}
          />
        )}
        {properties && properties.length > 0 && (
          <MyPropertiesTable
            properties={properties}
            selectedProperties={selectedProperties}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
          />
        )}
        {!properties && (
          <div className="py-4 text-center">
            <Spinner animation="border" variant="primary"/>
          </div>
        )}
        {total > 25 && (
          <Card.Footer className="d-flex justify-content-center border-top-0">
            <Paginator currentPage={page} perPage={25} total={total} onSelect={setPage} />
          </Card.Footer>
        )}
      </Card>
      <OffMarketPropertyOffcanvas fromConsole={false} />
    </Provider>
  );
}
